import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Chip,
  Tooltip,
  CircularProgress,
  Alert,
  InputAdornment,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { inventoryAdjustmentAPI, InventoryAdjustmentLogItem } from '../services/api';
import { formatDateTimeForDisplay } from '../utils/dateUtils';

const ADJUSTMENT_TYPE_LABELS: Record<string, string> = {
  manual: 'Ajuste Manual',
  physical_count: 'Conteo Fisico',
  damage: 'Dano',
  expired: 'Vencido',
  theft: 'Robo/Perdida',
  error_correction: 'Correccion de Error',
  other: 'Otro',
};

const ADJUSTMENT_TYPE_COLORS: Record<string, 'default' | 'primary' | 'warning' | 'error' | 'info' | 'success'> = {
  manual: 'primary',
  physical_count: 'info',
  damage: 'error',
  expired: 'warning',
  theft: 'error',
  error_correction: 'warning',
  other: 'default',
};

const InventoryAdjustments: React.FC = () => {
  const [items, setItems] = useState<InventoryAdjustmentLogItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchSku, setSearchSku] = useState('');
  const [debouncedSku, setDebouncedSku] = useState('');

  // Debounce SKU search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSku(searchSku);
      setPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchSku]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await inventoryAdjustmentAPI.getHistory(
        page * rowsPerPage,
        rowsPerPage,
        debouncedSku || undefined
      );
      setItems(data.items);
      setTotal(data.total);
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        setError(detail);
      } else if (Array.isArray(detail)) {
        setError(detail.map((e: any) => e.msg).join(', '));
      } else {
        setError('Error al cargar historial de ajustes');
      }
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSku]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Ajustes de Inventario
        </Typography>
        <TextField
          size="small"
          placeholder="Buscar por SKU..."
          value={searchSku}
          onChange={(e) => setSearchSku(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: 280 }}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Fecha</TableCell>
                <TableCell>Usuario</TableCell>
                <TableCell>SKU</TableCell>
                <TableCell>Insumo</TableCell>
                <TableCell align="right">Anterior</TableCell>
                <TableCell align="right">Nuevo</TableCell>
                <TableCell align="right">Cambio</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Razon</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {debouncedSku
                        ? `No se encontraron ajustes para SKU "${debouncedSku}"`
                        : 'No hay ajustes de inventario registrados'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {formatDateTimeForDisplay(item.created_at)}
                    </TableCell>
                    <TableCell>
                      {item.adjusted_by_user_name || 'Sistema'}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace" fontWeight={600}>
                        {item.supply_sku}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={item.supply_name}>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {item.supply_name}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">{item.previous_quantity}</TableCell>
                    <TableCell align="right">{item.new_quantity}</TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color={
                          item.adjustment_quantity > 0
                            ? 'success.main'
                            : item.adjustment_quantity < 0
                              ? 'error.main'
                              : 'text.primary'
                        }
                      >
                        {item.adjustment_quantity > 0 ? '+' : ''}
                        {item.adjustment_quantity}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={ADJUSTMENT_TYPE_LABELS[item.adjustment_type] || item.adjustment_type}
                        color={ADJUSTMENT_TYPE_COLORS[item.adjustment_type] || 'default'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={item.reason}>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 220,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {item.reason}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[25, 50, 100]}
          labelRowsPerPage="Filas por pagina:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `mas de ${to}`}`
          }
        />
      </Paper>
    </Container>
  );
};

export default InventoryAdjustments;
